import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import {
    AdditiveBlending,
    Color,
    NormalBlending,
    Object3D,
    Vector2,
    type ColorRepresentation,
} from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js'
import { characterOutlineTransitionDuration } from '@/src/game/config/constants'

export type OutlineHandle = {
    setEnabled: (enabled: boolean) => void
    syncVisibility: () => void
    setThickness: (thickness: number) => void
    setStrength: (strength: number) => void
    setGlow: (glow: number) => void
    setPulsePeriod: (pulsePeriod: number) => void
    setDownSampleRatio: (downSampleRatio: number) => void
    setAdditive: (additive: boolean) => void
    setColor: (color: ColorRepresentation) => void
    dispose: () => void
}

type OutlineControllerState = {
    additive: boolean
    color: Color
    downSampleRatio: number
    disposed: boolean
    enabled: boolean
    glow: number
    pulsePeriod: number
    strength: number
    thickness: number
}

type OutlineInterpolatedState = {
    additive: boolean
    color: Color
    downSampleRatio: number
    glow: number
    pulsePeriod: number
    strength: number
    thickness: number
}

export type CharacterOutlineController = {
    handle: OutlineHandle
    state: OutlineControllerState
}

type OutlinePassWithShaderPatch = OutlinePass & {
    edgeDetectionMaterial: {
        fragmentShader: string
        needsUpdate: boolean
    }
}

const OUTLINE_THICKNESS_MULTIPLIER = 170
const MIN_OUTLINE_THICKNESS = 0.65
const MAX_OUTLINE_THICKNESS = 3.9
const MOBILE_OUTLINE_REFERENCE_SHORT_SIDE = 390
const MOBILE_OUTLINE_SCALE_MIN = 0.85
const MOBILE_OUTLINE_SCALE_MAX = 1.15

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max)
}

function computeOutlineThickness(
    logicalThickness: number,
    responsiveThickness: boolean,
    width: number,
    height: number,
) {
    if (logicalThickness <= 0) {
        return 0
    }

    const baseThickness = Math.max(
        logicalThickness * OUTLINE_THICKNESS_MULTIPLIER,
        MIN_OUTLINE_THICKNESS,
    )

    if (!responsiveThickness) {
        return Math.min(baseThickness, MAX_OUTLINE_THICKNESS)
    }

    const shortSide = Math.max(1, Math.min(width, height))
    const mobileScale = clamp(
        shortSide / MOBILE_OUTLINE_REFERENCE_SHORT_SIDE,
        MOBILE_OUTLINE_SCALE_MIN,
        MOBILE_OUTLINE_SCALE_MAX,
    )

    return Math.min(baseThickness * mobileScale, MAX_OUTLINE_THICKNESS)
}

function updateFxaaResolution(
    fxaaPass: ShaderPass,
    width: number,
    height: number,
    pixelRatio: number,
) {
    const resolution = fxaaPass.material.uniforms.resolution?.value
    if (!resolution) {
        return
    }

    resolution.x = 1 / Math.max(width * pixelRatio, 1)
    resolution.y = 1 / Math.max(height * pixelRatio, 1)
}

function disableOccludedOutlineRendering(outlinePass: OutlinePass) {
    const passWithShaderPatch = outlinePass as OutlinePassWithShaderPatch
    const edgeDetectionMaterial = passWithShaderPatch.edgeDetectionMaterial

    if (
        !edgeDetectionMaterial?.fragmentShader.includes('vec3 edgeColor = 1.0 - visibilityFactor > 0.001 ? visibleEdgeColor : hiddenEdgeColor;')
    ) {
        return
    }

    edgeDetectionMaterial.fragmentShader = edgeDetectionMaterial.fragmentShader.replace(
        'vec3 edgeColor = 1.0 - visibilityFactor > 0.001 ? visibleEdgeColor : hiddenEdgeColor;\n\t\t\t\t\tgl_FragColor = vec4(edgeColor, 1.0) * vec4(d);',
        'float visibleFactor = 1.0 - visibilityFactor > 0.001 ? 1.0 : 0.0;\n\t\t\t\t\tvec3 edgeColor = mix(hiddenEdgeColor, visibleEdgeColor, visibleFactor);\n\t\t\t\t\tgl_FragColor = vec4(edgeColor, visibleFactor) * vec4(d);',
    )
}

export function createCharacterOutlineController(
    initialThickness: number,
    initialColor: string,
    initialStrength: number,
    initialGlow: number,
    initialPulsePeriod: number,
    initialDownSampleRatio: number,
    initialAdditive: boolean,
): CharacterOutlineController {
    const state: OutlineControllerState = {
        additive: initialAdditive,
        color: new Color(initialColor),
        downSampleRatio: initialDownSampleRatio,
        disposed: false,
        enabled: true,
        glow: initialGlow,
        pulsePeriod: initialPulsePeriod,
        strength: initialStrength,
        thickness: initialThickness,
    }

    const handle: OutlineHandle = {
        setEnabled(enabled) {
            state.enabled = enabled
        },

        syncVisibility() {
            // OutlinePass reads actual mesh visibility during rendering, so nothing to sync.
        },

        setThickness(thickness) {
            state.thickness = thickness
        },

        setStrength(strength) {
            state.strength = strength
        },

        setGlow(glow) {
            state.glow = glow
        },

        setPulsePeriod(pulsePeriod) {
            state.pulsePeriod = pulsePeriod
        },

        setDownSampleRatio(downSampleRatio) {
            state.downSampleRatio = Math.max(1, downSampleRatio)
        },

        setAdditive(additive) {
            state.additive = additive
        },

        setColor(color) {
            state.color.set(color)
        },

        dispose() {
            state.disposed = true
        },
    }

    return {
        handle,
        state,
    }
}

export function CharacterOutlineEffect({
    controller,
    target,
    responsiveThickness = false,
}: {
    controller: CharacterOutlineController
    target: Object3D | null
    responsiveThickness?: boolean
}) {
    const camera = useThree((state) => state.camera)
    const gl = useThree((state) => state.gl)
    const scene = useThree((state) => state.scene)
    const size = useThree((state) => state.size)

    const composerRef = useRef<EffectComposer | null>(null)
    const outlinePassRef = useRef<OutlinePass | null>(null)
    const fxaaPassRef = useRef<ShaderPass | null>(null)
    const downSampleRatioRef = useRef(controller.state.downSampleRatio)
    const interpolatedStateRef = useRef<OutlineInterpolatedState>({
        additive: controller.state.additive,
        color: controller.state.color.clone(),
        downSampleRatio: controller.state.downSampleRatio,
        glow: controller.state.glow,
        pulsePeriod: controller.state.pulsePeriod,
        strength: controller.state.strength,
        thickness: controller.state.thickness,
    })

    useEffect(() => {
        const composer = new EffectComposer(gl)
        const renderPass = new RenderPass(scene, camera)
        const outlinePass = new OutlinePass(
            new Vector2(size.width, size.height),
            scene,
            camera,
        )
        const fxaaPass = new ShaderPass(FXAAShader)
        const outputPass = new OutputPass()

        disableOccludedOutlineRendering(outlinePass)
        outlinePass.edgeGlow = 0
        outlinePass.pulsePeriod = 0
        outlinePass.usePatternTexture = false
        outlinePass.downSampleRatio = Math.max(1, controller.state.downSampleRatio)
        outlinePass.visibleEdgeColor.copy(controller.state.color)
        outlinePass.hiddenEdgeColor.copy(controller.state.color)
        outlinePass.edgeThickness = computeOutlineThickness(
            controller.state.thickness,
            responsiveThickness,
            size.width,
            size.height,
        )
        outlinePass.edgeStrength = controller.state.strength
        outlinePass.overlayMaterial.blending = controller.state.additive
            ? AdditiveBlending
            : NormalBlending

        const pixelRatio = gl.getPixelRatio()
        composer.setPixelRatio(pixelRatio)
        updateFxaaResolution(fxaaPass, size.width, size.height, pixelRatio)
        fxaaPass.enabled = true
        composer.addPass(renderPass)
        composer.addPass(outlinePass)
        composer.addPass(fxaaPass)
        composer.addPass(outputPass)

        composerRef.current = composer
        outlinePassRef.current = outlinePass
        fxaaPassRef.current = fxaaPass

        return () => {
            outlinePass.selectedObjects = []
            outlinePassRef.current = null
            fxaaPassRef.current = null
            composerRef.current = null
            composer.dispose()
        }
    }, [camera, controller, gl, responsiveThickness, scene, size.height, size.width])

    useEffect(() => {
        const pixelRatio = gl.getPixelRatio()
        composerRef.current?.setPixelRatio(pixelRatio)
        composerRef.current?.setSize(size.width, size.height)
        outlinePassRef.current?.setSize(size.width, size.height)
        if (fxaaPassRef.current) {
            updateFxaaResolution(fxaaPassRef.current, size.width, size.height, pixelRatio)
        }
    }, [gl, size.height, size.width])

    useFrame((_, delta) => {
        const composer = composerRef.current
        const outlinePass = outlinePassRef.current
        const fxaaPass = fxaaPassRef.current

        if (!composer || !outlinePass || !fxaaPass || controller.state.disposed) {
            return
        }

        fxaaPass.enabled = true

        const smoothingAlpha = characterOutlineTransitionDuration <= 0
            ? 1
            : 1 - Math.exp(-delta / characterOutlineTransitionDuration)
        const interpolatedState = interpolatedStateRef.current
        interpolatedState.color.lerp(controller.state.color, smoothingAlpha)
        interpolatedState.thickness +=
            (controller.state.thickness - interpolatedState.thickness) * smoothingAlpha
        interpolatedState.strength +=
            (controller.state.strength - interpolatedState.strength) * smoothingAlpha
        interpolatedState.glow +=
            (controller.state.glow - interpolatedState.glow) * smoothingAlpha
        interpolatedState.pulsePeriod +=
            (controller.state.pulsePeriod - interpolatedState.pulsePeriod) * smoothingAlpha
        interpolatedState.downSampleRatio +=
            (controller.state.downSampleRatio - interpolatedState.downSampleRatio) * smoothingAlpha
        const additiveBlendDifference = Math.abs(
            controller.state.strength - interpolatedState.strength,
        ) + Math.abs(
            controller.state.glow - interpolatedState.glow,
        ) + Math.abs(
            controller.state.thickness - interpolatedState.thickness,
        )
        if (controller.state.additive === interpolatedState.additive) {
            // keep
        } else if (additiveBlendDifference < 0.15) {
            interpolatedState.additive = controller.state.additive
        }

        const edgeThickness = computeOutlineThickness(
            interpolatedState.thickness,
            responsiveThickness,
            size.width,
            size.height,
        )
        outlinePass.selectedObjects =
            controller.state.enabled && target && edgeThickness > 0 ? [target] : []
        const nextDownSampleRatio = Math.max(
            1,
            Math.round(interpolatedState.downSampleRatio),
        )
        if (downSampleRatioRef.current !== nextDownSampleRatio) {
            downSampleRatioRef.current = nextDownSampleRatio
            outlinePass.downSampleRatio = nextDownSampleRatio
            outlinePass.setSize(size.width, size.height)
        } else {
            outlinePass.downSampleRatio = nextDownSampleRatio
        }
        outlinePass.edgeGlow = interpolatedState.glow
        outlinePass.pulsePeriod = interpolatedState.pulsePeriod
        outlinePass.visibleEdgeColor.copy(interpolatedState.color)
        outlinePass.hiddenEdgeColor.copy(interpolatedState.color)
        outlinePass.edgeThickness = edgeThickness
        outlinePass.edgeStrength = interpolatedState.strength
        outlinePass.overlayMaterial.blending = interpolatedState.additive
            ? AdditiveBlending
            : NormalBlending

        composer.render()
    }, 1)

    return null
}

